import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Typography,
  Button,
  useTheme,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { selectFunds } from "../../Redux/Reducers/selectors";

const FundsBlacklistTable = ({
  rules,
  onAdd,
  onEdit,
  onDelete,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  selectedFund,
  onFundChange,
}) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState("rule");
  const [order, setOrder] = useState("asc");
  const funds = useSelector(selectFunds);

  // Sort funds by abbreviation in ascending order
  const sortedFunds = funds
    ? [...funds].sort((a, b) =>
        (a.abbreviation || "").localeCompare(b.abbreviation || "")
      )
    : [];

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onPageSizeChange(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const filteredRules = rules.filter((rule) => {
    // First filter by selected fund - only show rules if a fund is selected
    if (!selectedFund) {
      return false;
    }
    if (rule.fund !== selectedFund) {
      return false;
    }
    // Then filter by search term
    return Object.values(rule).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const sortedRules = [...filteredRules].sort((a, b) => {
    const aValue = a[orderBy] || "";
    const bValue = b[orderBy] || "";

    if (order === "asc") {
      return aValue.toString().localeCompare(bValue.toString());
    } else {
      return bValue.toString().localeCompare(aValue.toString());
    }
  });

  const paginatedRules = sortedRules.slice(
    page * pageSize,
    page * pageSize + pageSize
  );
  return (
    <Box>
      {/* Toolbar with Fund Selection and Add Rule button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Autocomplete
          options={sortedFunds}
          getOptionLabel={(option) => option.abbreviation}
          value={
            sortedFunds.find((fund) => fund.abbreviation === selectedFund) ||
            null
          }
          onChange={(e, newValue) => {
            onFundChange && onFundChange(newValue ? newValue.abbreviation : "");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Fund"
              variant="outlined"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onAdd(selectedFund)}
          disabled={!selectedFund}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "white",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            "&:disabled": {
              backgroundColor: theme.palette.action.disabled,
              color: theme.palette.action.disabledBackground,
            },
          }}
        >
          Add Rule
        </Button>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  width: "30%",
                  minWidth: "400px",
                }}
                onClick={() => handleRequestSort("rule")}
              >
                Rules
                {orderBy === "rule" && (
                  <span style={{ marginLeft: 8 }}>
                    {order === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  width: "10%",
                  minWidth: "50px",
                }}
                onClick={() => handleRequestSort("strategy")}
              >
                Strategy
                {orderBy === "strategy" && (
                  <span style={{ marginLeft: 8 }}>
                    {order === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                  width: "15%",
                  minWidth: "150px",
                }}
                onClick={() => handleRequestSort("ticker")}
              >
                Ticker
                {orderBy === "ticker" && (
                  <span style={{ marginLeft: 8 }}>
                    {order === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 600,
                  textAlign: "center",
                  width: "10%",
                  minWidth: "100px",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRules.map((rule) => (
              <TableRow
                key={rule.id}
                sx={{
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <TableCell>{rule.rule}</TableCell>
                <TableCell>{rule.strategy}</TableCell>
                <TableCell>{rule.ticker}</TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Tooltip title="Edit Rule" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(rule)}
                      sx={{
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.light + "20",
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Rule" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(rule)}
                      sx={{
                        color: theme.palette.error.main,
                        "&:hover": {
                          backgroundColor: theme.palette.error.light + "20",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {paginatedRules.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {!selectedFund
                      ? "Please select a fund to view blacklist rules"
                      : "No blacklist rules found for this fund"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredRules.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Rows per page:"
      />
    </Box>
  );
};

export default FundsBlacklistTable;
